/**
 * The Space weather page's small charts as inline SVG (classes `.sw-chart*` in styles.css, brand chart and QC
 * tokens). The shapes, vertical scales and time marks are the domain's (`space-weather-charts.ts`), the same as
 * Teslyn Field draws. Each SVG stretches to the width of its panel, so a phone shows the whole day; the scale's words
 * sit beside it in the page's own type (never stretched); each chart has its words for screen readers.
 */
import type { ReactElement } from 'react';
import {
  SW_CHART_WIDTH as W,
  kpBarsGeometry,
  traceGeometry,
  type AxisTick,
  type TimeTick,
} from '@teslyn/domain';

const pct = (v: number, of: number): string => `${((v / of) * 100).toFixed(2)}%`;

/** The vertical scale's words beside the plot (the plot's grid: the unit above the scale, the scale beside the frame, the time marks under it). */
function Scale({
  ticks,
  height,
}: {
  readonly ticks: readonly AxisTick[];
  readonly height: number;
}): ReactElement {
  return (
    <div className="sw-chart__ticks" aria-hidden="true">
      {ticks.map((t) => (
        <span key={t.value} className="sw-chart__tick caption data" style={{ top: pct(t.y, height) }}>
          {t.label}
        </span>
      ))}
    </div>
  );
}

/** The time marks under the plot. */
function Times({ times }: { readonly times: readonly TimeTick[] }): ReactElement {
  return (
    <div className="sw-chart__times" aria-hidden="true">
      {times.map((t) => (
        <span
          key={t.x}
          className={`sw-chart__time caption muted${t.x < 1 ? ' sw-chart__time--start' : t.x > W - 1 ? ' sw-chart__time--end' : ''}`}
          style={{ left: pct(t.x, W) }}
        >
          {t.label}
        </span>
      ))}
    </div>
  );
}

function Grid({ ticks }: { readonly ticks: readonly AxisTick[] }): ReactElement {
  return (
    <>
      {ticks.map((t) => (
        <line
          key={t.value}
          className="sw-chart__grid"
          x1={0}
          x2={W}
          y1={t.y}
          y2={t.y}
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </>
  );
}

export function TraceChart({
  label,
  unit,
  points,
  points2,
  names,
  fromMs,
  toMs,
  zero = false,
  height = 90,
}: {
  readonly label: string;
  readonly unit: string;
  readonly points: readonly { readonly t: number; readonly v: number | null }[];
  /** A second line on the same scale (Bt beside Bz). */
  readonly points2?: readonly { readonly t: number; readonly v: number | null }[];
  /** The two lines' names for the key ("Bz", "Bt"). */
  readonly names?: readonly [string, string];
  readonly fromMs: number;
  readonly toMs: number;
  readonly zero?: boolean;
  readonly height?: number;
}): ReactElement {
  const g = traceGeometry({
    label,
    unit,
    points,
    ...(points2 ? { points2 } : {}),
    fromMs,
    toMs,
    height,
    zero,
  });
  return (
    <figure className="sw-chart">
      <figcaption className="caption muted">{label}</figcaption>
      <div className="sw-chart__plot">
        <span className="sw-chart__unit caption muted" aria-hidden="true">
          {unit}
        </span>
        <Scale ticks={g.ticks} height={height} />
        <div className="sw-chart__frame">
          <svg
            viewBox={`0 0 ${W} ${height}`}
            preserveAspectRatio="none"
            role="img"
            aria-label={g.description}
            className="sw-chart__svg"
          >
            <Grid ticks={g.ticks} />
            {g.zeroY !== null && (
              <line
                className="sw-chart__zero"
                x1={0}
                x2={W}
                y1={g.zeroY}
                y2={g.zeroY}
                vectorEffect="non-scaling-stroke"
              />
            )}
            {g.paths2.map((d, i) => (
              <path
                key={`b${i}`}
                className="sw-chart__line sw-chart__line--2"
                d={d}
                vectorEffect="non-scaling-stroke"
              />
            ))}
            {g.paths.map((d, i) => (
              <path key={i} className="sw-chart__line" d={d} vectorEffect="non-scaling-stroke" />
            ))}
          </svg>
        </div>
        <Times times={g.times} />
      </div>
      {names && (
        <p className="caption muted sw-chart__key">
          <span className="sw-chart__swatch" aria-hidden="true" /> {names[0]}{' '}
          <span className="sw-chart__swatch sw-chart__swatch--2" aria-hidden="true" /> {names[1]}
        </p>
      )}
    </figure>
  );
}

export function KpBars({
  bins,
  likelyKp,
  daylight,
  fromMs,
  nowMs,
}: {
  readonly bins: readonly { readonly t: string; readonly kp: number }[];
  readonly likelyKp: number;
  readonly daylight: readonly (readonly [number, number])[];
  readonly fromMs: number;
  readonly nowMs: number;
}): ReactElement {
  const h = 160;
  const g = kpBarsGeometry({ bins, likelyKp, daylight, fromMs, nowMs, height: h });
  return (
    <figure className="sw-chart">
      <div className="sw-chart__plot">
        <span className="sw-chart__unit caption muted" aria-hidden="true">
          Kp
        </span>
        <Scale ticks={g.ticks} height={h} />
        <div className="sw-chart__frame">
          <svg
            viewBox={`0 0 ${W} ${h}`}
            preserveAspectRatio="none"
            role="img"
            aria-label={g.description}
            className="sw-chart__svg sw-chart__svg--kp"
          >
            {g.daylight.map((d, i) => (
              <rect key={i} className="sw-chart__day" x={d.x} y={0} width={d.width} height={h} />
            ))}
            <Grid ticks={g.ticks} />
            {g.bars.map((b) => (
              <rect
                key={b.t}
                className={b.high ? 'sw-chart__bar sw-chart__bar--high' : 'sw-chart__bar'}
                x={b.x}
                y={b.y}
                width={b.width}
                height={b.height}
              />
            ))}
            <line
              className="sw-chart__limit"
              x1={0}
              x2={W}
              y1={g.likelyY}
              y2={g.likelyY}
              vectorEffect="non-scaling-stroke"
            />
            {g.nowX !== null && (
              <line
                className="sw-chart__now"
                x1={g.nowX}
                x2={g.nowX}
                y1={0}
                y2={h}
                vectorEffect="non-scaling-stroke"
              />
            )}
          </svg>
          <span className="sw-chart__limit-label caption" style={{ top: pct(g.likelyY, h) }}>
            {g.likelyLabel}
          </span>
        </div>
        <Times times={g.times} />
      </div>
    </figure>
  );
}
